export type DateValue = Date | string | number | null | undefined;

const numericDatePattern = /^\d+$/;
const timeZoneSuffixPattern = /(Z|[+-]\d{2}:?\d{2})$/i;

/**
 * Les dates issues de l'API Castorama sont stockées en UTC dans des colonnes
 * sans fuseau. En l'absence de suffixe, elles doivent donc être interprétées
 * comme UTC et non comme une heure locale du navigateur.
 */
export function parseUtcDate(value: DateValue): Date {
  if (value instanceof Date) {
    return new Date(value.getTime());
  }

  if (typeof value === 'number') {
    return new Date(value);
  }

  const normalized = value?.trim();
  if (!normalized) {
    return new Date(Number.NaN);
  }

  // L'API HBM peut renvoyer un timestamp sous forme de chaîne.
  if (numericDatePattern.test(normalized)) {
    return new Date(Number(normalized));
  }

  const utcValue = timeZoneSuffixPattern.test(normalized) ? normalized : `${normalized}Z`;
  return new Date(utcValue);
}

/**
 * Retourne un timestamp toujours comparable. Une date invalide est placée en
 * fin de liste au lieu de produire un comparateur NaN qui conserverait les
 * projets groupés par source.
 */
export function utcDateTimestamp(value: DateValue): number {
  const timestamp = parseUtcDate(value).getTime();
  return Number.isNaN(timestamp) ? Number.NEGATIVE_INFINITY : timestamp;
}

/**
 * Variante destinée à l'affichage : une valeur absente ou non interprétable
 * renvoie `null` plutôt qu'une `Date` invalide, qui ferait lever le pipe
 * `date` d'Angular (NG02100) et casserait le rendu de toute la liste.
 */
export function parseUtcDateOrNull(value: DateValue): Date | null {
  const date = parseUtcDate(value);
  return Number.isNaN(date.getTime()) ? null : date;
}
